import {apiFetch, type ApiResponse} from "./api";

/**
 * Кабинет компаний. Отдельная сессия: у компании свой тип токена
 * (`company_access`), поэтому её нельзя класть в `accessToken` рядом с
 * пользовательским — иначе apiFetch отправит компанейский токен в
 * пользовательские эндпоинты, получит 401 и выкинет человека на лендинг.
 *
 * Все запросы идут с `{public: true}`: так apiFetch не подставляет
 * пользовательский Authorization и, главное, не запускает refresh-флоу и
 * редирект на лендинг при 401. Заголовок компании ставим руками.
 */

const COMPANY_TOKEN_KEY = "companyToken";

export const companyTokenStorage = {
    get: (): string | null => localStorage.getItem(COMPANY_TOKEN_KEY),
    set: (token: string): void => localStorage.setItem(COMPANY_TOKEN_KEY, token),
    clear: (): void => localStorage.removeItem(COMPANY_TOKEN_KEY),
};

export type Company = {
    id: string;
    name: string;
    email: string;
    phone: string | null;
    contactName: string | null;
    description: string | null;
    logoUrl: string | null;
    inn: string | null;
    ogrn: string | null;
    kpp: string | null;
    isVerified: boolean;
    balance: number;
};

/** Место компании. `moderationStatus` — что показывать в кабинете. */
export type CompanyPlace = {
    id: string;
    name: string;
    description: string;
    category: string;
    address: string;
    city: string | null;
    latitude: number | null;
    longitude: number | null;
    photoUrl: string | null;
    photos: string[];
    phone: string | null;
    website: string | null;
    isFree: boolean;
    price: number;
    minAge: number | null;
    timeInfo: string | null;
    externalUrl: string | null;
    isActive: boolean;
    isFeatured: boolean;
    moderationStatus: "published" | "pending";
    placementStart: string | null;
    placementEnd: string | null;
    createdAt: string | null;
    updatedAt: string | null;
};

export type CompanyPlacesPage = {
    total: number;
    published: number;
    pending: number;
    places: CompanyPlace[];
};

/** Поля, которыми владелец вправе управлять: публикацию выдаёт модерация. */
export type CompanyPlaceInput = {
    name: string;
    description: string;
    category: string;
    address: string;
    city: string | null;
    /** Сериализуется в JSON-ключ `yandexMapsUrl` — так называется поле на бэкенде (CompanyPlaceBody.yandex_maps_url, alias yandexMapsUrl). */
    yandexMapsUrl: string | null;
    photoUrl: string | null;
    photos: string | null;
    phone: string | null;
    website: string | null;
    isFree: boolean;
    price: number;
    minAge: number | null;
    timeInfo: string | null;
    externalUrl: string | null;
    /** Только для тарифа `event` — дата/время начала и окончания мероприятия. */
    placementStart?: string | null;
    placementEnd?: string | null;
};

function companyFetch<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    const token = companyTokenStorage.get();
    const headers: Record<string, string> = {...(options.headers as Record<string, string> | undefined)};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return apiFetch<T>(endpoint, {...options, headers}, {public: true});
}

type AuthResult = {token: string; company: Company};

export async function registerCompany(body: {
    name: string;
    email: string;
    password: string;
    phone: string;
    contactName: string;
    inn?: string | null;
    ogrn?: string | null;
    kpp?: string | null;
}): Promise<ApiResponse<AuthResult>> {
    return companyFetch<AuthResult>("/api/company/register", {
        method: "POST",
        body: JSON.stringify(body),
    });
}

export async function loginCompany(email: string, password: string): Promise<ApiResponse<AuthResult>> {
    return companyFetch<AuthResult>("/api/company/login", {
        method: "POST",
        body: JSON.stringify({email, password}),
    });
}

export async function getCompanyMe(): Promise<ApiResponse<{company: Company}>> {
    return companyFetch<{company: Company}>("/api/company/me", {method: "GET"});
}

export async function listCompanyPlaces(): Promise<ApiResponse<CompanyPlacesPage>> {
    return companyFetch<CompanyPlacesPage>("/api/company/places", {method: "GET"});
}

export async function getCompanyPlace(id: string): Promise<ApiResponse<{place: CompanyPlace}>> {
    return companyFetch<{place: CompanyPlace}>(`/api/company/places/${id}`, {method: "GET"});
}

export async function createCompanyPlace(body: CompanyPlaceInput): Promise<ApiResponse<{place: CompanyPlace}>> {
    return companyFetch<{place: CompanyPlace}>("/api/company/places", {
        method: "POST",
        body: JSON.stringify(body),
    });
}

export async function updateCompanyPlace(
    id: string,
    body: CompanyPlaceInput,
): Promise<ApiResponse<{place: CompanyPlace}>> {
    return companyFetch<{place: CompanyPlace}>(`/api/company/places/${id}`, {
        method: "PUT",
        body: JSON.stringify(body),
    });
}

export async function deleteCompanyPlace(id: string): Promise<ApiResponse<{deleted: boolean}>> {
    return companyFetch<{deleted: boolean}>(`/api/company/places/${id}`, {method: "DELETE"});
}

export type PlacementTariff = "event" | "1m" | "3m";

export type PlacementOrder = {
    id: string;
    placeId: number | null;
    tariff: PlacementTariff;
    amountMinor: number;
    status: "draft" | "pending_payment" | "pending_moderation" | "approved" | "rejected" | "expired";
    rejectionReason: string | null;
    placementStart: string | null;
    placementEnd: string | null;
    createdAt: string | null;
};

export async function createPlacementOrder(
    placeId: string,
    tariff: PlacementTariff,
    promoCode?: string,
): Promise<ApiResponse<{orderId: string; paymentUrl: string; status: string}>> {
    return companyFetch(`/api/company/places/${placeId}/placement-orders`, {
        method: "POST",
        body: JSON.stringify(promoCode ? {tariff, promoCode} : {tariff}),
    });
}

export async function getPlacementOrder(
    orderId: string,
): Promise<ApiResponse<{order: PlacementOrder; place: CompanyPlace | null}>> {
    return companyFetch(`/api/company/placement-orders/${orderId}`, {method: "GET"});
}
